import React from 'react';
import { Controller } from 'react-hook-form';
import InputMC from '../../../components/InputMC';
import DropdownMC from '../../../components/DropdownMC';
import ToggleSwitchMC from '../../../components/ToggleSwitchMC';
import CardMC from '../../../components/CardMC';
import CheckBoxMC from '../../../components/CheckBoxMC';
import ButtonMC from '../../../components/ButtonMC';
import IconMC from '../../../components/IconMC';
import TableMC from '../../../components/TableMC';
import { renderJsonWithChips } from '../utils/renderUtils';
import WebhookAuthSection from './WebhookAuthSection';

const WebhookFormView = ({
  methods,
  t,
  eventType,
  eventTypeOptions,
  availableEvents,
  handleEventToggle,
  headers,
  handleEditHeaders,
  handleRemoveHeaders,
  jsonBody,
  handleEditJson,
  handleRemoveJson,
}) => {
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Header Section */}
      <div className="tw-flex tw-flex-col">
        <h3 className="tw-text-base tw-font-semibold tw-text-gray-950 tw-mb-0">
          {t('event_title')}
        </h3>
        <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
          {t('event_description')}
        </p>
      </div>

      {/* Name Section */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col">
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
            {t('name_label')}
          </h4>
          <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
            {t('name_description')}
          </p>
        </div>
        <InputMC
          name="name"
          label={t('name_floating_label')}
          placeholder={t('name_placeholder')}
          useFloatingLabel
        />
      </div>

      {/* Target URL Section */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col">
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
            {t('target_url_label')}
          </h4>
          <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
            {t('target_url_description')}
          </p>
        </div>
        <InputMC
          name="target_url"
          label={t('target_url_label')}
          placeholder={t('target_url_placeholder')}
          useFloatingLabel
        />
      </div>

      {/* Webhook Authentication Section */}
      <WebhookAuthSection methods={methods} t={t} />

      {/* Active Section */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Controller
            name="isActive"
            control={methods.control}
            render={({ field }) => (
              <ToggleSwitchMC
                isOn={field.value}
                handleToggle={field.onChange}
                size="small"
              />
            )}
          />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
            {t('active_label')}
          </h4>
        </div>
        <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
          {t('active_description')}
        </p>
      </div>

      {/* Event Type Section */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col">
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
            {t('event_type_label')}
          </h4>
          <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
            {t('event_type_description')}
          </p>
        </div>
        <DropdownMC
          id="event_type"
          name="event_type"
          label={t('event_type_label')}
          placeholder={t('event_type_placeholder')}
          options={eventTypeOptions}
          defaultValue={methods.getValues('event_type')}
          useFloatingLabel
        />
      </div>

      {/* Events to Send Section */}
      {availableEvents.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <div className="tw-flex tw-flex-col">
            <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
              {t('events_to_send_title')}
            </h4>
            <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
              {t('events_to_send_description')}
            </p>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Controller
              name="events"
              control={methods.control}
              render={({ field }) => (
                <>
                  {availableEvents.map((event) => (
                    <CardMC key={event.id} className="tw-p-4 tw-flex tw-justify-between tw-items-center">
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-sm tw-font-semibold tw-text-gray-950">
                          {event.label}
                        </span>
                        <span className="tw-text-xs tw-text-gray-700">
                          {event.description}
                        </span>
                      </div>
                      <CheckBoxMC
                        id={event.id}
                        checked={field.value.includes(event.id)}
                        onChange={(e) => handleEventToggle(event.id, e.target.checked, field.onChange, field.value)}
                        className="tw-cursor-pointer"
                      />
                    </CardMC>
                  ))}
                  {methods.formState.errors.events && (
                    <p className="tw-text-xs tw-text-red-500 tw-mt-1">
                      {methods.formState.errors.events.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
        </div>
      )}

      {/* Webhook Header Section */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
            {t('webhook_header_title')}
          </h4>
          <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
            {t('webhook_header_description')}
          </p>
        </div>
        {headers && headers.length > 0 ? (
          <div className="tw-py-2 tw-px-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <span className="tw-text-sm tw-font-semibold tw-text-gray-600">
                {t('header_label')}
              </span>
              <div className="tw-flex tw-gap-2">
                <button type="button" onClick={handleEditHeaders} className="tw-text-gray-500 hover:tw-text-gray-700">
                  <IconMC name="edit-outline" size="base" />
                </button>
                <button type="button" onClick={handleRemoveHeaders} className="tw-text-gray-500 hover:tw-text-red-500">
                  <IconMC name="trash-2-outline" size="base" />
                </button>
              </div>
            </div>

            <TableMC
              borderAll
              headerClassName="!tw-py-1"
              cellClassName="!tw-py-1"
              headers={[
                { key: 'key', label: t('header_name_label') },
                { key: 'value', label: t('header_value_label') }
              ]}
              data={headers.map((h, i) => ({
                id: i,
                key: { type: 'text', value: h.key, className: 'tw-font-medium' },
                value: { type: 'custom-component', value: renderJsonWithChips(h.value) }
              }))}
            />
          </div>
        ) : (
          <div className="tw-flex tw-justify-start">
            <ButtonMC
              text={t('edit_header_button')}
              variant="SECONDARY"
              outlined
              onClick={handleEditHeaders}
              className="tw-w-fit"
            />
          </div>
        )}
      </div>

      {/* Webhook Body Section */}
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
            {t('webhook_body_title')}
          </h4>
          <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
            {t('webhook_body_description')}
          </p>
        </div>
        {jsonBody ? (
          <div className="tw-relative tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-group">
            <div className="tw-absolute tw-top-3 tw-right-3 tw-flex tw-gap-2">
              <button type="button" onClick={handleEditJson} className="tw-text-gray-500 hover:tw-text-gray-700">
                <IconMC name="edit-outline" size="base" />
              </button>
              <button type="button" onClick={handleRemoveJson} className="tw-text-gray-500 hover:tw-text-red-500">
                <IconMC name="trash-2-outline" size="base" />
              </button>
            </div>
            <pre className="tw-font-mono tw-text-sm tw-text-gray-800 tw-whitespace-pre-wrap tw-mb-0">
              {renderJsonWithChips(jsonBody)}
            </pre>
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-justify-start">
              <ButtonMC
                text={t('edit_json_button')}
                variant="SECONDARY"
                outlined
                onClick={handleEditJson}
                className="tw-w-fit"
              />
            </div>
            {methods.formState.errors.jsonBody && (
              <p className="tw-text-xs tw-text-red-500">
                {methods.formState.errors.jsonBody.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookFormView;
